import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { Search, Eye, Link as LinkIcon, Trash2 } from 'lucide-react';
import AdminLayout from '../../components/admin/AdminLayout';
import DeleteListingModal from '../../components/DeleteListingModal';
import { useListingStore } from '../../stores/listingStore';
import { formatPrice, formatDate } from '../../utils/formatters';

const AdminListings = () => {
  const { listings, removeListing } = useListingStore();
  const [searchTerm, setSearchTerm] = useState('');
  const [filter, setFilter] = useState('all');
  const [deleteListingId, setDeleteListingId] = useState<string | null>(null);

  const filteredListings = listings.filter(listing => {
    const matchesSearch = listing.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         listing.location.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesFilter = filter === 'all' || listing.status === filter;
    return matchesSearch && matchesFilter;
  });

  const handleViewListing = (id: string) => {
    window.open(`/listings/${id}`, '_blank');
  };

  const handleDeleteClick = (id: string) => {
    setDeleteListingId(id);
  };

  const handleDeleteConfirm = () => {
    if (deleteListingId) {
      removeListing(deleteListingId);
      setDeleteListingId(null);
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'active':
        return 'bg-green-100 text-green-800';
      case 'pending':
        return 'bg-yellow-100 text-yellow-800';
      case 'inactive':
        return 'bg-red-100 text-red-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  return (
    <AdminLayout>
      <div className="space-y-6">
        {/* Filters */}
        <div className="flex flex-col sm:flex-row gap-4">
          <div className="flex-1">
            <div className="relative">
              <Search className="absolute left-3 top-2.5 h-5 w-5 text-gray-400" />
              <input
                type="text"
                placeholder="Search listings..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-indigo-500 focus:border-indigo-500"
              />
            </div>
          </div>
          <div className="flex gap-4">
            <select
              value={filter}
              onChange={(e) => setFilter(e.target.value)}
              className="px-4 py-2 border border-gray-300 rounded-lg focus:ring-indigo-500 focus:border-indigo-500"
            >
              <option value="all">All Status</option>
              <option value="active">Active</option>
              <option value="pending">Pending</option>
              <option value="inactive">Inactive</option>
            </select>
          </div>
        </div>

        {/* Listings Table */}
        <div className="bg-white rounded-xl shadow-sm overflow-hidden">
          <div className="min-w-full divide-y divide-gray-200">
            <div className="bg-gray-50">
              <div className="grid grid-cols-12 gap-4 px-6 py-3">
                <div className="col-span-4 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Listing
                </div>
                <div className="col-span-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Price
                </div>
                <div className="col-span-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Status
                </div>
                <div className="col-span-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Listed Date
                </div>
                <div className="col-span-2 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Actions
                </div>
              </div>
            </div>

            <div className="divide-y divide-gray-200 bg-white">
              {filteredListings.map((listing) => (
                <div key={listing.id} className="grid grid-cols-12 gap-4 px-6 py-4 hover:bg-gray-50">
                  <div className="col-span-4">
                    <div className="flex items-center">
                      <div className="h-16 w-16 flex-shrink-0">
                        <img
                          src={listing.images[0]}
                          alt={listing.title}
                          className="h-16 w-16 rounded-lg object-cover"
                        />
                      </div>
                      <div className="ml-4">
                        <div className="text-sm font-medium text-gray-900">
                          {listing.title}
                        </div>
                        <div className="text-sm text-gray-500">{listing.location}</div>
                      </div>
                    </div>
                  </div>
                  <div className="col-span-2 flex items-center">
                    <div>
                      <div className="text-sm font-medium text-gray-900">
                        {formatPrice(listing.price)}
                      </div>
                      <div className="text-sm text-gray-500">per month</div>
                    </div>
                  </div>
                  <div className="col-span-2 flex items-center">
                    <span className={`px-2.5 py-1 inline-flex text-xs leading-5 font-medium rounded-full ${getStatusColor(listing.status)}`}>
                      {listing.status.charAt(0).toUpperCase() + listing.status.slice(1)}
                    </span>
                  </div>
                  <div className="col-span-2 flex items-center text-sm text-gray-500">
                    {formatDate(listing.available)}
                  </div>
                  <div className="col-span-2 flex items-center justify-end space-x-2">
                    <button
                      onClick={() => handleViewListing(listing.id)}
                      className="p-1.5 text-gray-600 hover:text-gray-900 rounded-full hover:bg-gray-100 transition-colors"
                      title="View Listing"
                    >
                      <Eye className="h-5 w-5" />
                    </button>
                    <Link
                      to={`/edit-listing/${listing.id}`}
                      className="p-1.5 text-indigo-600 hover:text-indigo-900 rounded-full hover:bg-indigo-50 transition-colors"
                      title="Edit Listing"
                    >
                      <LinkIcon className="h-5 w-5" />
                    </Link>
                    <button
                      onClick={() => handleDeleteClick(listing.id)}
                      className="p-1.5 text-red-600 hover:text-red-900 rounded-full hover:bg-red-50 transition-colors"
                      title="Delete Listing"
                    >
                      <Trash2 className="h-5 w-5" />
                    </button>
                  </div>
                </div>
              ))}

              {filteredListings.length === 0 && (
                <div className="px-6 py-8 text-center">
                  <p className="text-gray-500">No listings found</p>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Delete Confirmation Modal */}
      {deleteListingId && (
        <DeleteListingModal
          onConfirm={handleDeleteConfirm}
          onCancel={() => setDeleteListingId(null)}
          listingTitle={listings.find(l => l.id === deleteListingId)?.title || 'this listing'}
        />
      )}
    </AdminLayout>
  );
};

export default AdminListings;